import { ResponsivePie } from "@nivo/pie";
import { BasicTooltip } from "@nivo/tooltip";
import React, { memo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { CloseIcon } from "../../../Common/SvgIcons/WorkflowIcons";
import NivoTextParser from "./NivoTextParser";
import nivoTheme, { NIVO_CHART_TEXT_COLOR } from "./nivoTheme";

const formatValueWithUnit = (value, unit) => {
  const normalizedUnit = String(unit || "").trim();

  if (!normalizedUnit) return value;
  if (["%", "°"].includes(normalizedUnit)) return `${value}${normalizedUnit}`;

  return `${value} ${normalizedUnit}`;
};

const PieChart = memo(function PieChart({ data, customColor, unit, showLegend, height = 250 }) {
  const normalizedUnit = String(unit || "").trim();

  // make sure parent container have a defined height when using
  // responsive component, otherwise height will be 0 and
  // no chart will be rendered.
  // website examples showcase many properties,
  // you'll often use just a few of them.
  return (
    <div style={{ height }}>
      <ResponsivePie
        data={data}
        animate={false}
        valueFormat={normalizedUnit
          ? (value) => formatValueWithUnit(value, normalizedUnit)
          : undefined}
        tooltip={normalizedUnit
          ? ({ datum }) => (
            <BasicTooltip
              id={datum.label}
              value={datum.formattedValue}
              enableChip
              color={datum.color}
            />
          )
          : undefined}
        colors={{ scheme: !customColor ? "red_yellow_blue" : "set3" }}
        margin={{ top: 40, right: 40, bottom: showLegend ? 70 : 40, left: 50 }}
        innerRadius={0.5}
        padAngle={0.7}
        cornerRadius={customColor ? 0 : 5}
        activeOuterRadiusOffset={8}
        borderWidth={1}
        borderColor={{
          from: "color",
          modifiers: [["darker", 0.2]],
        }}
        arcLinkLabel="id"
        arcLinkLabelsSkipAngle={10}
        arcLinkLabelsTextColor={NIVO_CHART_TEXT_COLOR}
        arcLinkLabelsOffset={-10}
        arcLinkLabelsThickness={2}
        arcLinkLabelsStraightLength={8}
        arcLinkLabelsDiagonalLength={16}
        arcLinkLabelsColor={{ from: "color" }}
        enableArcLabels={!normalizedUnit}
        arcLabel="formattedValue"
        arcLabelsSkipAngle={10}
        arcLabelsTextColor={NIVO_CHART_TEXT_COLOR}
        theme={nivoTheme}
        legends={showLegend ? [
          {
            anchor: "bottom",
            direction: "row",
            justify: false,
            translateY: 42,
            itemsSpacing: 8,
            itemWidth: 100,
            itemHeight: 18,
            itemTextColor: "var(--text-color)",
            itemDirection: "left-to-right",
            itemOpacity: 1,
            symbolSize: 10,
            symbolShape: "circle",
          },
        ] : []}
      />
    </div>
  );
});

function PieNivo({ data, title, custom_color, borderless = false, unit, showLegend = false }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <div
        className={`chart ${borderless ? "p-0" : "badge-border p-4"}`}
        style={{ height: "400px", position: "relative", cursor: "pointer" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsModalOpen(true)}
      >
        <NivoTextParser title={title} />
        {isHovered && (
          <button
            type="button"
            className="plain-button chart-expand-control"
            aria-label={`Expand ${title || "pie chart"}`}
            title="Expand"
            onClick={(event) => {
              event.stopPropagation();
              setIsModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        )}
        <PieChart data={data} customColor={custom_color} unit={unit} showLegend={showLegend} />
      </div>

      {isModalOpen && (
        <div
          className="chart-full-view-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`${title || "Pie chart"} full view`}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="container-card chart-full-view-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="plain-button text-danger chart-full-view-close"
              aria-label="Close full view"
              title="Close full view"
              onClick={() => setIsModalOpen(false)}
            >
              <CloseIcon className="wf-icon text-danger" />
            </button>
            <div className="chart-full-view-body">
              <NivoTextParser title={title} />
              <PieChart
                data={data}
                customColor={custom_color}
                unit={unit}
                showLegend={showLegend}
                height="calc(100% - 50px)"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default memo(PieNivo);
